import React from 'react';
import { number, string } from 'prop-types';
import classNames from 'classnames';

import css from './MaterialSymbol.module.css';

// Material Symbols Outlined Unicode codepoints (hex), keyed by icon name. Must
// stay in sync with the `icon_names` subset on the <link> in public/index.html.
// Source: https://github.com/google/material-design-icons (variablefont
// MaterialSymbolsOutlined[...].codepoints).
const CODEPOINTS = {
  account_circle: 0xf20b,
  add: 0xe145,
  calendar_month: 0xebcc,
  call: 0xf0d4,
  beach_access: 0xeb3e,
  check: 0xe668,
  check_circle: 0xf0be,
  chevron_left: 0xe5cb,
  chevron_right: 0xe5cc,
  close: 0xe5cd,
  content_copy: 0xe14d,
  door_front: 0xeffd,
  edit: 0xf097,
  event_busy: 0xe615,
  event_repeat: 0xeb7b,
  expand_more: 0xe5cf,
  gpp_maybe: 0xf014,
  info: 0xe88e,
  lock: 0xe899,
  mail: 0xe159,
  map: 0xe55b,
  outdoor_grill: 0xea47,
  person: 0xf0d3,
  apps: 0xe5c3,
  home: 0xe88a,
  pets: 0xe91d,
  pool: 0xeb48,
  rainy: 0xf176,
  schedule: 0xefd6,
  search: 0xef7a,
  sell: 0xf05b,
  shield: 0xe9e0,
  star: 0xf09a,
  sunny: 0xe81a,
  thermometer: 0xe846,
  trending_up: 0xe8e5,
  tune: 0xe429,
  verified: 0xef76,
  weekend: 0xe16b,
  workspace_premium: 0xe7af,
  yard: 0xf089,
};

/**
 * Renders a Material Symbols (Outlined) icon from the Google Fonts subset.
 *
 * Usage:
 *   <MaterialSymbol name="pool" />
 *   <MaterialSymbol name="search" size={28} fill={1} title="Buscar" />
 *
 * The icon name is the one shown at https://fonts.google.com/icons (snake_case,
 * e.g. "pool", "calendar_month", "chevron_right"). Colour is inherited from the
 * parent's CSS `color`.
 *
 * IMPORTANT: the font is subset to only the icons listed in `icon_names` on the
 * Material Symbols <link> in public/index.html. Before using a NEW icon, add its
 * name to that list (kept alphabetically sorted) AND its Unicode codepoint to the
 * `CODEPOINTS` map below, or it will render blank.
 * The request fixes GRAD at 0, so the `grade` prop only supports 0 unless the
 * <link>'s GRAD axis range is widened.
 *
 * We render the icon's Unicode codepoint (not its ligature name) as the span's
 * text. Both resolve to the same glyph, but rendering the codepoint keeps the
 * literal name (e.g. "account_circle") out of the DOM so crawlers that don't load
 * the icon font — Google's, notably — can't index it as readable text. Codepoints
 * come from the official Material Symbols Outlined `.codepoints` file.
 *
 * @param {Object} props
 * @param {string} props.name Material Symbols icon name (snake_case)
 * @param {string?} props.className applied on top of the base classes
 * @param {string?} props.rootClassName replaces the default layout class
 * @param {number} props.size pixel size (font-size) of the glyph (default 24)
 * @param {0|1} props.fill 0 = outlined, 1 = filled (default 0)
 * @param {number} props.weight stroke weight 100–700 (default 400)
 * @param {number} props.grade emphasis grade, e.g. -25, 0, 200 (default 0)
 * @param {number} props.opticalSize optical size 20–48 (default 24)
 * @param {string?} props.title accessible label; when omitted the icon is decorative
 */
const MaterialSymbol = props => {
  const { name, className, rootClassName, size, fill, weight, grade, opticalSize, title } = props;

  const classes = classNames('material-symbols-outlined', rootClassName || css.root, className);

  // Render the Unicode codepoint so the literal icon name never reaches the DOM.
  // Fall back to the ligature name for any icon not yet in CODEPOINTS.
  const codepoint = CODEPOINTS[name];
  const glyph = codepoint ? String.fromCodePoint(codepoint) : name;

  const style = {
    fontSize: `${size}px`,
    fontVariationSettings: `'FILL' ${fill}, 'wght' ${weight}, 'GRAD' ${grade}, 'opsz' ${opticalSize}`,
  };

  return (
    <span
      className={classes}
      style={style}
      translate="no"
      role={title ? 'img' : undefined}
      aria-label={title || undefined}
      aria-hidden={title ? undefined : true}
    >
      {glyph}
    </span>
  );
};

MaterialSymbol.defaultProps = {
  className: null,
  rootClassName: null,
  size: 24,
  fill: 0,
  weight: 400,
  grade: 0,
  opticalSize: 24,
  title: null,
};

MaterialSymbol.propTypes = {
  name: string.isRequired,
  className: string,
  rootClassName: string,
  size: number,
  fill: oneOfZeroOne,
  weight: number,
  grade: number,
  opticalSize: number,
  title: string,
};

export default MaterialSymbol;

// 0 (outlined) or 1 (filled)
function oneOfZeroOne(props, propName, componentName) {
  const value = props[propName];
  if (value !== 0 && value !== 1) {
    return new Error(
      `Invalid prop \`${propName}\` supplied to \`${componentName}\`. Expected 0 or 1.`
    );
  }
  return null;
}
